// frontend/src/pages/asset-admin/PendingReturns.js
/**
 * Asset Admin - Pending Returns Management Page
 * 
 * Custodians can view and manage pending return requests from employees.
 * Theme: Red (Admin)
 *
 * The card + confirm/reject modals are shared with the HR Portal's
 * "Pending Returns" tab on /admin/asset-inventory — see
 * frontend/src/components/asset/PendingReturnsShared.js. This page adds
 * the full-page chrome: header, stats, search, grid/list view toggle, and
 * the table view.
 */

import React, { useState } from 'react';
import {
  RotateCcw, CheckCircle, XCircle, Clock, Calendar,
  AlertCircle, Grid3X3, List,
} from 'lucide-react';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';
import PillToggle from '../../components/UI/PillToggle';
import { usePersistence } from '../../hooks/usePersistence';
import { 
  usePendingReturnsForCustodian, 
  useConfirmReturn, 
  useRejectReturn 
} from '../../hooks/useAssetInventoryData';
import { formatDate } from '../../utils/dateUtils';
import { StatusBadge } from '../../components/UI/StatusBadge';
import {
  PendingReturnCard,
  PendingReturnCategoryIcon as CategoryIcon,
  ConfirmReturnModal,
  RejectReturnModal,
} from '../../components/asset/PendingReturnsShared';

// Per-category circle colors for the list view (the shared card uses a
// uniform amber-on-orange palette which doesn't differentiate categories at
// the row scale — the table benefits from category cues).
const getCategoryColors = (code) => {
  const colors = {
    IT: { bg: 'bg-blue-100', text: 'text-blue-600' },
    ACC: { bg: 'bg-purple-100', text: 'text-purple-600' },
    FLT: { bg: 'bg-green-100', text: 'text-green-600' },
    LIC: { bg: 'bg-amber-100', text: 'text-amber-600' },
    OFF: { bg: 'bg-gray-100', text: 'text-gray-600' },
    FUEL: { bg: 'bg-orange-100', text: 'text-orange-600' },
    DSL: { bg: 'bg-teal-100', text: 'text-teal-600' },
  };
  return colors[code] || { bg: 'bg-gray-100', text: 'text-gray-600' };
};

// ============================================
// VIEW MODE HELPERS
// ============================================
const getDefaultViewMode = () => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('asset-admin-pending-returns-viewMode');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed === 'grid' || parsed === 'list') return parsed;
      } catch (e) { /* use default */ }
    }
    return window.innerWidth < 768 ? 'grid' : 'list';
  }
  return 'grid';
};

// ============================================
// MAIN COMPONENT
// ============================================
const PendingReturns = () => {
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = usePersistence('asset-admin-pending-returns-viewMode', getDefaultViewMode());
  const [confirmModal, setConfirmModal] = useState({ open: false, transfer: null });
  const [rejectModal, setRejectModal] = useState({ open: false, transfer: null });
  
  // Data fetching
  const { data: pendingReturns = [], isLoading, error, refetch } = usePendingReturnsForCustodian();
  
  // Mutations
  const confirmReturn = useConfirmReturn();
  const rejectReturn = useRejectReturn();
  
  // Filter returns by search
  const filteredReturns = pendingReturns.filter(ret => {
    if (!search.trim()) return true;
    const searchLower = search.toLowerCase();
    return (
      ret.asset_tag?.toLowerCase().includes(searchLower) ||
      ret.from_employee?.first_name?.toLowerCase().includes(searchLower) ||
      ret.from_employee?.last_name?.toLowerCase().includes(searchLower) ||
      ret.from_employee?.staff_id?.toLowerCase().includes(searchLower)
    );
  });
  
  // Handlers
  const handleConfirmReturn = (data) => {
    confirmReturn.mutate({
      transferId: confirmModal.transfer.id,
      location: data.location,
      condition: data.condition || undefined,
      photos: data.photos,
    }, {
      onSuccess: () => {
        setConfirmModal({ open: false, transfer: null });
        refetch();
      },
    });
  };
  
  const handleRejectReturn = (data) => {
    rejectReturn.mutate({
      transferId: rejectModal.transfer.id,
      reason: data.reason,
    }, {
      onSuccess: () => {
        setRejectModal({ open: false, transfer: null });
        refetch();
      },
    });
  };
  
  if (isLoading) {
    return (
      <div className="space-y-6 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="animate-pulse flex items-center gap-4">
            <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
            <div className="space-y-2">
              <div className="h-6 bg-gray-200 rounded w-48"></div>
              <div className="h-4 bg-gray-200 rounded w-64"></div>
            </div>
          </div>
        </div>
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-xl border p-4 animate-pulse">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                  <div className="space-y-2">
                    <div className="h-5 bg-gray-200 rounded w-24"></div>
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border overflow-hidden animate-pulse">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="grid grid-cols-7 gap-4">
                {[1, 2, 3, 4, 5, 6, 7].map(i => (
                  <div key={i} className="h-4 bg-gray-200 rounded w-20"></div>
                ))}
              </div>
            </div>
            <div className="divide-y divide-gray-100">
              {[1, 2, 3].map(i => (
                <div key={i} className="px-6 py-4">
                  <div className="grid grid-cols-7 gap-4 items-center">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                    <div className="h-4 bg-gray-200 rounded w-28"></div>
                    <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-red-700">Failed to load pending returns. Please try again.</p>
          <button onClick={() => refetch()} className="btn btn-outline mt-4">
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6 bg-gray-50 min-h-screen">
      {/* Page Header */}
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <RotateCcw className="w-4 h-4 sm:w-6 sm:h-6 text-orange-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">Pending Returns</h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                Review and process asset return requests from employees
              </p>
            </div>
          </div>
          
          <div className="flex items-center justify-between gap-3">
            {/* Left: Search */}
            <DebouncedSearchInput
              value={search}
              onSearch={setSearch}
              placeholder="Search by asset or employee..."
              className="max-w-xl"
            />

            {/* Right: View toggle */}
            <div className="flex items-center gap-2">
              <PillToggle
                value={viewMode}
                onChange={setViewMode}
                options={[
                  { id: 'grid', label: 'Grid view', icon: Grid3X3 },
                  { id: 'list', label: 'List view', icon: List },
                ]}
                variant="icon"
                theme="admin"
                ariaLabel="View mode"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
            </div>
            <div className="min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{pendingReturns.length}</p>
              <p className="text-xs sm:text-sm text-gray-500 truncate">Total Pending</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            </div>
            <div className="min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">
                {pendingReturns.filter(r => ['faulty', 'damaged'].includes(r.return_reason)).length}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 truncate">Faulty/Damaged</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" />
            </div>
            <div className="min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">
                {pendingReturns.filter(r => {
                  if (!r.expires_at) return false;
                  const expires = new Date(r.expires_at);
                  return (expires - new Date()) < 24 * 60 * 60 * 1000;
                }).length}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 truncate">Expiring Soon</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className={`${viewMode === 'list' ? 'bg-white rounded-2xl shadow-sm border overflow-hidden' : ''}`}>
        {filteredReturns.length === 0 ? (
          <div className={`text-center py-12 ${viewMode === 'grid' ? 'bg-white rounded-2xl shadow-sm border' : ''}`}>
            <RotateCcw className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {search ? 'No returns match your search' : 'No pending return requests'}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredReturns.map(returnTransfer => (
              <PendingReturnCard
                key={returnTransfer.id}
                returnTransfer={returnTransfer}
                onConfirm={(t) => setConfirmModal({ open: true, transfer: t })}
                onReject={(t) => setRejectModal({ open: true, transfer: t })}
              />
            ))}
          </div>
        ) : (
          <>
            {/* Mobile card list (list view, below md) — reuses the shared card
                so confirm/reject stay one tap away in the storeroom */}
            <div className="md:hidden space-y-3 p-4">
              {filteredReturns.map(returnTransfer => (
                <PendingReturnCard
                  key={returnTransfer.id}
                  returnTransfer={returnTransfer}
                  onConfirm={(t) => setConfirmModal({ open: true, transfer: t })}
                  onReject={(t) => setRejectModal({ open: true, transfer: t })}
                />
              ))}
            </div>
            {/* Desktop table (md and up) */}
            <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="table-header border-b">
                <tr>
                  <th className="table-header-cell">Asset</th>
                  <th className="table-header-cell">Employee</th>
                  <th className="table-header-cell">Reason</th>
                  <th className="table-header-cell">Notes</th>
                  <th className="table-header-cell">Requested</th>
                  <th className="table-header-cell">Expires</th>
                  <th className="table-header-cell text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredReturns.map(returnTransfer => {
                  const expiresAt = returnTransfer.expires_at ? new Date(returnTransfer.expires_at) : null;
                  const isExpiringSoon = expiresAt && (expiresAt - new Date()) < 24 * 60 * 60 * 1000;
                  const categoryCode = returnTransfer.asset_detail?.asset_type?.category?.code;
                  const colors = getCategoryColors(categoryCode);

                  return (
                    <tr key={returnTransfer.id} className="table-row">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 ${colors.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
                            <CategoryIcon code={categoryCode} className={`w-4 h-4 ${colors.text}`} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{returnTransfer.asset_tag}</p>
                            <p className="text-xs text-gray-500">{returnTransfer.asset_type_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-900">
                          {returnTransfer.from_employee?.first_name} {returnTransfer.from_employee?.last_name}
                        </p>
                        <p className="text-xs text-gray-500">{returnTransfer.from_employee?.staff_id}</p>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={returnTransfer.return_reason} />
                      </td>
                      <td className="px-4 py-3">
                        {returnTransfer.notes ? (
                          <p className="text-sm text-gray-600 max-w-[200px] truncate" title={returnTransfer.notes}>
                            {returnTransfer.notes}
                          </p>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Calendar className="w-3 h-3" />
                          {formatDate(returnTransfer.transfer_date)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {expiresAt ? (
                          <span className={`text-sm ${isExpiringSoon ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                            {formatDate(expiresAt)}
                            {isExpiringSoon && ' (Soon!)'}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setRejectModal({ open: true, transfer: returnTransfer })}
                            className="btn btn-sm btn-outline text-xs flex gap-1"
                          >
                            <XCircle className="w-3 h-3" />
                            Reject
                          </button>
                          <button
                            onClick={() => setConfirmModal({ open: true, transfer: returnTransfer })}
                            className="btn btn-sm btn-success text-xs flex gap-1"
                          >
                            <CheckCircle className="w-3 h-3" />
                            Confirm
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </>
        )}
      </div>
      
      {/* Modals */}
      <ConfirmReturnModal
        key={confirmModal.transfer?.id ?? 'confirm-closed'}
        isOpen={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, transfer: null })}
        returnTransfer={confirmModal.transfer}
        onConfirm={handleConfirmReturn}
        isLoading={confirmReturn.isPending}
      />
      
      <RejectReturnModal
        isOpen={rejectModal.open}
        onClose={() => setRejectModal({ open: false, transfer: null })}
        returnTransfer={rejectModal.transfer}
        onReject={handleRejectReturn}
        isLoading={rejectReturn.isPending}
      />
    </div>
  );
};

export default PendingReturns;
